import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Fab,
  Chip,
  Stack,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SignHealthDocumentModal from '../../../../components/signHealthDocumentModal/signHealthDocumentModal';
import { useSignedDocumentActions } from '../../../../hooks/useSignedDocumentActions';
import { useSignAttested } from '../hooks/useSignAttested';
import { useSignedAttestedDocument } from '../hooks/useSignedAttestedDocument';
import AddIcon from '@mui/icons-material/Add';
import AttestedModal from '../attestedModal/attestedModalRouter';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import { useAttesteds } from '../hooks/useAttesteds';
import {
  formatAttestedType,
  formatCid,
  formatDaysOfRest,
  formatPeriod,
  formatText,
  isRestType,
} from '../attestedFields';

export default function AttestedListMobile() {
  const { patientId } = useParams();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isModalOpen, setModalOpen] = useState(false);

  const { attesteds, isLoading, refetch: getAttestedsFromPatient } = useAttesteds(patientId);

  const saveAttested = (returnFromApi) => {
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    setModalOpen(false);
    getAttestedsFromPatient();
  };

  const { sign: signAttested } = useSignAttested();
  const { fetchUrl: fetchSignedAttestedUrl } = useSignedAttestedDocument();
  const {
    documentToSign: attestedToSign,
    openSignModal,
    closeSignModal,
    onSigned,
    openSignedDocument,
    isOpeningSignedDocument,
  } = useSignedDocumentActions({
    fetchSignedDocumentUrl: (attestedId) => fetchSignedAttestedUrl(patientId, attestedId),
    refetch: getAttestedsFromPatient,
    onSuccess: (message) => {
      setShowSuccessMessage(true);
      setSuccessMessage(message);
    },
    onError: (message) => {
      setShowErrorMessage(true);
      setErrorMessage(message);
    },
  });

  const printAttested = (attestedId) => {
    window.open(`/attested-to-print/${patientId}/${attestedId}`, '_blank');
  };

  return (
    <>
      <Loading isToHidden={isLoading === false && isOpeningSignedDocument === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
          Atestados
        </Typography>

        {attesteds.length === 0 ? (
          <Typography align="center" sx={{ py: 4 }}>
            Nenhum atestado cadastrado ainda.
          </Typography>
        ) : (
          <List disablePadding>
            {attesteds.map((attested) => (
              <ListItem
                key={attested.id}
                divider
                sx={{ pr: 12 }}
                secondaryAction={
                  <Stack direction="row">
                    {attested.signed ? (
                      <IconButton
                        color="primary"
                        aria-label="abrir PDF assinado"
                        onClick={() => openSignedDocument(attested.id)}
                      >
                        <PictureAsPdfOutlinedIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        color="primary"
                        aria-label="assinar digitalmente"
                        onClick={() => openSignModal(attested)}
                      >
                        <DrawOutlinedIcon />
                      </IconButton>
                    )}
                    <IconButton
                      color="primary"
                      aria-label="imprimir"
                      onClick={() => printAttested(attested.id)}
                    >
                      <PrintIcon />
                    </IconButton>
                  </Stack>
                }
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {formatText(attested.data)} — {formatAttestedType(attested)}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Profissional: {formatText(attested.nomeDoDentista)}
                      </Typography>
                      {isRestType(attested) ? (
                        <Typography variant="body2" color="text.secondary">
                          Dias de repouso: {formatDaysOfRest(attested)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Período: {formatPeriod(attested)}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        CID: {formatCid(attested)}
                      </Typography>
                      {attested.signed && (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<VerifiedUserOutlinedIcon />}
                          label={`Assinado em ${attested.signedAt}`}
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Fab
        color="primary"
        aria-label="adicionar atestado"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
        onClick={() => setModalOpen(true)}
      >
        <AddIcon />
      </Fab>

      <SignHealthDocumentModal
        open={!!attestedToSign}
        onClose={closeSignModal}
        onSigned={onSigned}
        onSign={(certificatePassword, cidAuthorizedByPatient) =>
          signAttested(patientId, attestedToSign.id, {
            certificatePassword,
            cidAuthorizedByPatient,
          })
        }
        title="Assinar atestado digitalmente"
        summary={
          attestedToSign
            ? `${formatAttestedType(attestedToSign)} de ${formatText(attestedToSign.data)} · ${formatText(attestedToSign.nomeDoDentista)}`
            : null
        }
        confirmationLabel={
          attestedToSign?.cid
            ? `O(a) paciente autorizou que o diagnóstico (CID ${attestedToSign.cid}) conste neste atestado.`
            : null
        }
        confirmationRequiredMessage="Confirme que o(a) paciente autorizou a inclusão do CID no atestado."
      />

      <AttestedModal
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        onAttestedSave={saveAttested}
      />
    </>
  );
}
